export type GraphQLError = {
    message: string;
    extensions?: Record<string, unknown>;
};

function getOperationName(query: string) {
    // https://regex101.com/r/SDICae/1
    const regex = new RegExp(/(?:query\s|mutation\s)(.*?)(?=\(|\s{)/);
    const matching = regex.exec(query);

    if (!matching) {
        return null;
    }

    return matching[1];
}

export type FetchGraphqlOptions = {
    signal?: AbortSignal;
    headers?: HeadersInit;
};

export type Extensions = {
    requestId: string;
};

type GraphqlResult<Query> = {
    data?: Query;
    errors?: GraphQLError[];
    extensions?: Extensions;
};

class AbortError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AbortError';
    }
}

export async function fetchGraphql<Query, Variables = Record<string, unknown>>(
    query: string | string[],
    variables?: Variables | Variables[],
    options: FetchGraphqlOptions = {}
): Promise<
    string extends typeof query ? GraphqlResult<Query> : GraphqlResult<Query>[]
> {
    try {
        const uri = process.env['NEXT_PUBLIC_GRAPHQL_API'];
        if (!uri) {
            throw new Error('Missing env var: NEXT_PUBLIC_GRAPHQL_API');
        }

        const apiKey =
            process.env['PORT'] === '3001'
                ? 'mock'
                : process.env['NEXT_PUBLIC_API_KEY'];
        if (!apiKey) {
            throw new Error('Missing env var: NEXT_PUBLIC_API_KEY');
        }

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'x-api-key': apiKey,
            ...(options.headers || {})
        };

        const isBatchedQuery = Array.isArray(query);

        const requestBody = isBatchedQuery
            ? query.map((q, index) => ({
                  query: q,
                  variables: variables && (variables as Variables[])[index],
                  operationName: getOperationName(q)
              }))
            : {
                  query,
                  variables,
                  operationName: getOperationName(query as string)
              };

        const response = await fetch(uri, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
            mode: 'cors',
            credentials: 'include',
            signal: options.signal
        });

        // We polyfill jsdom fetch with whatwg which requires an extra bit of
        // logic to throw if aborted after a resolved fetch,
        if (typeof options?.signal?.throwIfAborted === 'function') {
            options?.signal.throwIfAborted();
        } else {
            if (options?.signal?.aborted) {
                throw new AbortError('Operation aborted.');
            }
        }

        if (!response.ok) {
            return Promise.reject({
                error: {
                    name: 'InvalidResponse',
                    message: `invalid response: ${response.status}`
                }
            });
        }

        return await response.json();
    } catch (error) {
        if (error instanceof Error) {
            return Promise.reject({
                error: {
                    name: error.name,
                    message: error.message
                }
            });
        }

        return Promise.reject({
            error: {
                name: 'UnknownError',
                message: `unexpected error: ${error}`
            }
        });
    }
}

export default fetchGraphql;

type FormattedRejectionError = {
    error: {
        name: string;
        message: string;
    };
};

export function isFormattedRejectionError(
    error: unknown
): error is FormattedRejectionError {
    return (
        typeof error === 'object' &&
        error !== null &&
        'error' in error &&
        typeof (error as Record<string, unknown>)['error'] === 'object' &&
        typeof (error as Record<string, unknown>)['error'] !== null &&
        'name' in (error as { error: Record<string, unknown> }).error
    );
}
